import { useState, useCallback } from "react";
import {
  useGetSubmissionUploadUrl,
  useSaveStudentSubmissionFile,
} from "@oneapp/data";
import { useTranslation } from "react-i18next";
import { toISOWithOffset } from "../../../shared/utils";
import { useModuleData } from "../context/ModuleDataContext";
import { awaitStartExam } from "../../../shared/examSession/syncEngine";

interface UploadState {
  isUploading: boolean;
  error: string | null;
  isSuccess: boolean;
}

export const useUploadAnswerSheet = (examId: string) => {
  const { accessToken, profilesData } = useModuleData();
  const { t } = useTranslation("tests");
  const [state, setState] = useState<UploadState>({
    isUploading: false,
    error: null,
    isSuccess: false,
  });

  const studentId =
    profilesData?.unified_account_details?.profiles?.find(
      (profile) => profile.is_default_profile,
    )?.uam_id != null
      ? String(
          profilesData.unified_account_details.profiles.find(
            (profile) => profile.is_default_profile,
          )?.uam_id,
        )
      : profilesData?.unified_account_details?.profiles?.[0]?.uam_id != null
        ? String(profilesData.unified_account_details.profiles[0].uam_id)
        : "";

  const getAuthHeaders = useCallback(() => {
    const headers: Record<string, string> = {};
    if (accessToken) {
      headers.accesstoken = accessToken;
      headers.authorization = `Bearer ${accessToken}`;
    }
    return headers;
  }, [accessToken]);

  const submissionUploadUrlQuery = useGetSubmissionUploadUrl(
    examId,
    studentId,
    {
      query: {
        enabled: false,
        retry: false,
      },
      request: {
        clientName: "oneAsm",
        headers: getAuthHeaders(),
      },
    },
  );

  const { mutateAsync: saveStudentSubmissionFile } =
    useSaveStudentSubmissionFile({
      request: {
        clientName: "oneAsm",
        headers: getAuthHeaders(),
      },
    });

  const uploadFile = useCallback(
    async (file: File) => {
      setState({ isUploading: true, error: null, isSuccess: false });

      try {
        if (!examId || !studentId) {
          throw new Error(t("upload_answer_sheet_flow.upload_failed_retry"));
        }
        if (file.size === 0) {
          throw new Error(t("upload_answer_sheet_flow.upload_failed_retry"));
        }

        await awaitStartExam(examId);

        // Step 1: Get presigned upload URL
        const { data: uploadUrlData } =
          await submissionUploadUrlQuery.refetch();

        if (!uploadUrlData?.upload_url || !uploadUrlData?.file_key) {
          throw new Error(t("upload_answer_sheet_flow.upload_failed_retry"));
        }

        // Step 2: Upload file to S3
        await fetch(uploadUrlData.upload_url, {
          method: "PUT",
          body: file,
          headers: { "Content-Type": "application/pdf" },
        });

        // Step 3: Confirm upload
        await saveStudentSubmissionFile({
          data: {
            exam_id: examId,
            student_id: Number(studentId),
            file_path: uploadUrlData.file_key,
            file_uploaded_at: toISOWithOffset(new Date()),
          },
        });

        setState({ isUploading: false, error: null, isSuccess: true });
      } catch (err) {
        const message =
          err instanceof Error
            ? err.message
            : t("upload_answer_sheet_flow.upload_failed_retry");
        setState({ isUploading: false, error: message, isSuccess: false });
      }
    },
    [examId, studentId, saveStudentSubmissionFile, submissionUploadUrlQuery, t],
  );

  const reset = useCallback(() => {
    setState({ isUploading: false, error: null, isSuccess: false });
  }, []);

  return { ...state, uploadFile, reset };
};
